import {ResolvingLitElement} from "../../lib/ResolvingLitElement.js";
import {html} from "lit";
import {CustomerRepository} from "../../crm/CustomerRepository.js";
import {DateTime} from "luxon";
import {TimesheetEntryRepository} from "../TimesheetEntryRepository.js";
import {TimeTool} from "../../lib/TimeTool.js";
import {NO_CUSTOMER_ID} from "../TimesheetEntry.js";
import {entries} from "lodash-es";

export class PlanningPeriodWidget extends ResolvingLitElement {

    static properties = {
        currentMonth: { type: String },
    }

    /**
     * @private {ReactiveArray<TimesheetEntry>}
     */
    _entries;

    /**
     * @protected {ReactiveArray<PlannedTime>}
     */
    _plannedTimes;

    _futureMonths = 3;

    constructor() {
        super();
        this._numberFormatter = new Intl.NumberFormat('de-DE');
        this.currentMonth = DateTime.now().toFormat('yyyy-MM');
        /** @type {{stats:{customer:Customer,seconds:int,amount:float}[],totalSeconds:int,totalAmount:float}}} **/
        this._byCustomerStat = {
            stats: [],
            totalSeconds: 0,
            totalAmount: 0.0,
        };
    }

    render() {
        return html`<div class="card weekly-time-summary">
                <header class="row">
                    <h4 class="col-8">Planned by customer</h4><select name="month" class="right col-4" @change="${this._changeCurrentMonth}">${this._months().map(item => {return html`<option value="${item.value}">${item.option}</option>`})}</select>
                </header>
                <table class="striped next-bills">
                    <thead>
                        <tr>
                            <th></th>
                            <th>Hours</th>
                            <th>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                    ${this._byCustomerStat.stats.map( (item, key) => {
                        return html`<tr>
                            <th><a href="javascript:;" @click="${this._customerClicked}" data-customer-id="${item.customer.id}">${item.customer.name}</a></th>
                            <td>${this._formatSeconds(item.seconds)}</td>
                            <td>${item.amount ? this._numberFormatter.format(item.amount.toFixed(2)) + '€' : html`<span class="text-grey">0,00€</span>`}</td>
                        </tr>`
                    })}
                    </tbody>
                    <tfoot>
                        <tr>
                            <th></th>
                            <td>${this._totalHours()}</td>
                            <td><strong>${this._totalMoney()}</strong></td>
                        </tr>
                    </tfoot>
                </table>
            </div>`;
    }

    _customerClicked(evt) {
        this.dispatchEvent(new CustomEvent('customer-clicked', {detail: evt.target.dataset.customerId, bubbles:true,composed:true}));
    }

    connectedCallback() {
        super.connectedCallback();
        this._entries = this._get('timesheet-entries');
        this._plannedTimes = this._get('planned-times');
        this._months();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._plannedTimes.filled.delete(this._updateByRefill)
        this._plannedTimes.pushed.delete(this._updateByRefill);
        this._plannedTimes.itemUpdated.delete(this._updateByRefill);
    }

    firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);
        this._plannedTimes.filled.listen(this._updateByRefill);
        this._plannedTimes.pushed.listen(this._updateByRefill);
        this._plannedTimes.itemUpdated.listen(this._updateByRefill);
    }

    _months() {
        const months = [];
        let now = DateTime.now();//.toFormat('yyyy-MM')
        let next = DateTime.fromMillis(now.toMillis());

        for (let i = 1; i <= this._futureMonths; i++) {
            months.push({value:next.toFormat('yyyy-MM'), option: next.toFormat('yyyy/MM')});
            next = next.plus({months:1});
        }

        return months;

    }

    _totalMoney() {
        let money = this._byCustomerStat.totalAmount;
        return this._numberFormatter.format(money.toFixed(2)) + '€';
    }

    _totalHours() {
        const seconds = this._byCustomerStat.totalSeconds;
        return seconds ? TimeTool.formatDurationFromSeconds(seconds/3600, false) : '00:00';
    }

    /**
     * @param {Map<string,{customer:Customer,queryDate:Date,money:number,moneyFormatted:string,totalSeconds:number,billableSeconds:number,notBillableSeconds:number,dateWarning:boolean}>} stats
     * @param {PlannedTime[]} entries
     * @return {{stats: {customer:Customer,seconds:int,amount:float}[],totalSeconds:int,totalAmount:float}}
     * @private
     */
    _createStat(entries) {

        let stats = new Map();
        for (let plannedTime of entries) {
            if (!plannedTime.customer || !plannedTime.customer.id || plannedTime.customer.id === NO_CUSTOMER_ID) {
                continue;
            }
            if (!stats.has(plannedTime.customer.id)) {
                stats.set(plannedTime.customer.id, {
                    seconds:0,
                    amount:0,
                    customer: plannedTime.customer,
                });
            }
            let msDuration = plannedTime.end.getTime() - plannedTime.start.getTime()
            stats.get(plannedTime.customer.id).seconds += msDuration !== 0 ? Math.abs(msDuration) / 1000 : 0;
        }
        let totalSeconds = 0;
        let totalAmount = 0;
        for(let stat of stats.values()) {
            totalSeconds += stat.seconds;
            if (stat.seconds > 0 && stat.customer.hourlyRate) {
                stat.amount = stat.seconds/3600 * stat.customer.hourlyRate;
                totalAmount += stat.amount
            }
        }

        return {
            stats: Array.from(stats.values()),
            totalSeconds: totalSeconds,
            totalAmount: totalAmount,
        };

    }

    /**
     * @param {number} seconds
     * @return {TemplateResult<1>|string}
     * @private
     */
    _formatSeconds(seconds) {
        if (!seconds || seconds < 60) {
            return html`<span class="text-grey">00:00</span>`;
        }
        return TimeTool.formatDurationFromSeconds(seconds, false);
    }

    _updateByRefill = () => {
        this._byCustomerStat = this._createStat(this._plannedTimes.items());
        this.requestUpdate();
    };

    _changeCurrentMonth = (e) => {
        this.dispatchEvent(new CustomEvent('month-changed', {detail: e.target.value, bubbles: true}));
    };

    createRenderRoot() {
        return this;
    }
}

customElements.define('x-planning-period-widget', PlanningPeriodWidget);